import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { whoami, type Whoami } from './api';
import { currentSession, signOut, type Session } from './auth';
import { loadConfig, type PortalConfig } from './config';
import { ROLE_LABELS, StaffProvider } from './staff';
import { completeSso } from './sso';
import { AdminAccessPage } from './pages/AdminAccess';
import { AdminStaffPage } from './pages/AdminStaff';
import { AdminUsersPage } from './pages/AdminUsers';
import { AdminUserPage } from './pages/AdminUser';
import { ClaimInvitePage } from './pages/ClaimInvite';
import { FilesPage } from './pages/Files';
import { LoginPage } from './pages/Login';
import { MyAccountPage } from './pages/MyAccount';

export function App() {
  const [session, setSession] = useState<Session | undefined>();
  const [staff, setStaff] = useState<Whoami>();
  const [config, setConfig] = useState<PortalConfig>();
  const [ready, setReady] = useState(false);
  const [fatal, setFatal] = useState<string>();
  const [ssoError, setSsoError] = useState<string>();

  const refresh = useCallback(async () => {
    try {
      const loaded = await loadConfig();
      setConfig(loaded);

      // The SSO redirect lands back here carrying a one-time code, and it has to
      // be exchanged before anything asks whether someone is signed in --
      // otherwise the answer is no, the login form renders, and the code is lost.
      if (loaded.adminSso && window.location.pathname === loaded.adminSso.callbackPath) {
        try {
          await completeSso(loaded.adminSso, loaded.adminClientId, window.location.search);
        } catch (err) {
          // A refused or stale sign-in is not a broken portal. It belongs on the
          // login form, where there is something to do about it.
          setSsoError(err instanceof Error ? err.message : String(err));
        }
        // Drop the code from the address bar either way. Cognito rejects a reused
        // code, so reloading this URL would fail confusingly, and a credential
        // has no reason to sit in browser history.
        window.history.replaceState({}, '', '/');
      }

      const next = await currentSession();
      setSession(next);
      // Staff only: their role decides what the rest of the app renders, so it has
      // to be known before anything does. A partner has no role to fetch, and the
      // route would refuse their token anyway.
      setStaff(next?.audience === 'admin' ? await whoami() : undefined);
    } catch (err) {
      setFatal(err instanceof Error ? err.message : String(err));
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (fatal) {
    return (
      <main className="centered">
        <div className="card">
          <h1>Portal unavailable</h1>
          <p className="error">{fatal}</p>
        </div>
      </main>
    );
  }
  if (!ready) return <main className="centered">Loading…</main>;

  // Ahead of the sign-in gate, and deliberately not inside either Routes block
  // below. Someone accepting an invitation has no session yet -- a key-only
  // partner has no password to make one with -- so routing this through the
  // authenticated tree would show them the login form for an account they
  // cannot sign into. It still runs after the config fetch, because the page
  // reads the SFTP hostname to tell them where to connect.
  if (window.location.pathname === '/claim') {
    return <ClaimInvitePage config={config} />;
  }

  if (!session || !config) {
    return (
      <LoginPage config={config} ssoError={ssoError} onSignedIn={(next) => setSession(next)} />
    );
  }

  const onSignOut = async () => {
    await signOut();
    setSession(undefined);
    setStaff(undefined);
  };

  // The routes differ by audience rather than being guarded inside a shared set. A
  // partner has no admin route to be redirected away from, and the API would
  // refuse it anyway -- its admin routes only accept tokens from the other pool.
  //
  // Staff routes are not split further by role. Every role can open every admin
  // page; what a role changes is which controls those pages offer, and the files
  // page is the one exception because a role without files:list has nothing to
  // render there at all.
  if (session.audience === 'admin') {
    if (!staff) return <main className="centered">Loading…</main>;
    return (
      <StaffProvider staff={staff}>
        <Nav session={session} staff={staff} onSignOut={onSignOut} />
        <main>
          <Routes>
            <Route path="/" element={<Navigate to="/users" replace />} />
            <Route path="/users" element={<AdminUsersPage />} />
            <Route path="/users/:username" element={<AdminUserPage />} />
            <Route path="/access" element={<AdminAccessPage />} />
            {staff.capabilities.includes('staff:read') && (
              <Route path="/staff" element={<AdminStaffPage />} />
            )}
            {staff.capabilities.includes('files:list') && (
              <Route path="/files/*" element={<FilesPage audience="admin" />} />
            )}
            <Route path="*" element={<Navigate to="/users" replace />} />
          </Routes>
        </main>
      </StaffProvider>
    );
  }

  return (
    <>
      <Nav session={session} onSignOut={onSignOut} />
      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/files" replace />} />
          <Route path="/files/*" element={<FilesPage audience="partner" />} />
          <Route path="/account" element={<MyAccountPage config={config} />} />
          <Route path="*" element={<Navigate to="/files" replace />} />
        </Routes>
      </main>
    </>
  );
}

function Nav({
  session,
  staff,
  onSignOut,
}: {
  session: Session;
  staff?: Whoami;
  onSignOut: () => void;
}) {
  const { pathname } = useLocation();
  const links =
    session.audience === 'admin'
      ? [
          ['/users', 'Users'],
          ['/access', 'Access'],
          // Omitted rather than shown-and-refused for a role that cannot list
          // files: there is no route behind it, so it would land on the roster and
          // look like a bug.
          ...(staff?.capabilities.includes('staff:read') ? [['/staff', 'Staff']] : []),
          ...(staff?.capabilities.includes('files:list') ? [['/files', 'Files']] : []),
        ]
      : [
          ['/files', 'My files'],
          ['/account', 'My account'],
        ];

  return (
    <nav>
      <span className="brand">SFTP portal</span>
      {links.map(([to, label]) => (
        <Link key={to} to={to} className={pathname.startsWith(to) ? 'active' : undefined}>
          {label}
        </Link>
      ))}
      <span className="spacer" />
      <span className="who">
        {session.username}
        {/* The role, not just "staff": someone who cannot find the button they
            expected should be able to see why without asking. */}
        <span className="badge">
          {staff ? (ROLE_LABELS[staff.role] ?? staff.role) : 'partner'}
        </span>
      </span>
      <button type="button" className="link" onClick={onSignOut}>
        Sign out
      </button>
    </nav>
  );
}
